import React, { useContext } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import classes from './interviewCard.module.scss';
import getResizedImageUrl from '../../../helpers/getResizedImageUrl';
import DefaultAvatar from '../Candidates/CandidatesList/DefaultAvatar';
import AutoButton from '../../AutoButton';
import { UiContext } from '../../../context/UiContext';
import AdminService from '../../../services/AdminService';
import { JobsContext } from '../../../context/JobsContext';

export default function InterviewCard({
  event,
  user,
  refetch,
  onViewCandidate,
}) {
  const { t } = useTranslation();

  const { showFeedbackModal } = useContext(JobsContext);

  const eventDate = new Date(event.eventDate);
  const isToday = moment(eventDate).isSame(new Date(), 'day');

  const externalInterviewerId = event?.event?.externalInterviewers.find(
    (interviewer) => interviewer.userId === user?.id
  )?.id;

  const companyManagerId = event?.event?.companyManagers.find(
    (manager) => manager.userId === user?.id
  )?.id;

  const internalUserId = event?.event?.users.find(
    (usr) => usr.id === user?.id
  )?.id;

  const invitation = event?.event?.jobApplicationInterviewInvites.find(
    (invite) =>
      invite.externalInterviewerId === externalInterviewerId ||
      invite.companyManagerId === companyManagerId ||
      invite.userId === internalUserId
  );

  const feedback = event?.event?.jobApplicationInterviewFeedbacks.find(
    (fb) => fb.userId === user?.id
  );

  let adjustedStatus = event.status;
  if (invitation?.status === 'Pending' && event.status === 'Scheduled') {
    adjustedStatus = 'New';
  }

  if (invitation?.status === 'Pending' && event.status === 'Rescheduled') {
    adjustedStatus = 'Rescheduled';
  }

  if (invitation?.status === 'Declined' && event.status !== 'ChangeRequest') {
    adjustedStatus = 'Declined';
  }

  if (invitation?.status === 'Accepted') {
    adjustedStatus = 'Accepted';
  }

  if (Date.parse(event.eventDate) < new Date() && event.status !== 'Canceled') {
    adjustedStatus = 'Completed';
  }

  if (event.status === 'Canceled') {
    adjustedStatus = 'Canceled';
  }

  if (
    Date.parse(event.eventDate) < new Date() &&
    event.status !== 'Canceled' &&
    !feedback
  ) {
    adjustedStatus = 'FeedbackDue';
  }

  if (
    Date.parse(event.eventDate) < new Date() &&
    event.status !== 'Canceled' &&
    feedback
  ) {
    adjustedStatus = 'FeedbackSent';
  }

  if (
    Date.parse(event.eventDate) < new Date() &&
    event.status !== 'Canceled' &&
    feedback?.reviewedAt != null
  ) {
    adjustedStatus = 'FeedbackFinal';
  }

  const { showModal, showUnknownErrorModal, showNotification, setIsFetching } =
    useContext(UiContext);

  const declineInvite = () => {
    const prevStatus = invitation?.status;

    const undo = async () => {
      try {
        setIsFetching(true);
        await AdminService.changeJobApplicationInterviewInvite({
          interviewId: event?.id,
          jobApplicationId: event?.jobApplicationId,
          invitationid: invitation?.id,
          status: prevStatus,
        });
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        showUnknownErrorModal();
      } finally {
        setIsFetching(false);
      }
    };

    showModal({
      icon: 'delete',
      title: t('pages.InterviewDashboard.declineInvitation'),
      text: t('pages.InterviewDashboard.areYouSureYouWantToDecline'),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.changeJobApplicationInterviewInvite({
            interviewId: event?.id,
            jobApplicationId: event?.jobApplicationId,
            invitationid: invitation?.id,
            status: 'Declined',
          });

          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Calendar.EventDetails.Interview.Interview invitation declined'
            ),
            undo,
          });
        } catch (error) {
          showUnknownErrorModal();
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const acceptInvite = async () => {
    try {
      setIsFetching(true);
      await AdminService.changeJobApplicationInterviewInvite({
        interviewId: event?.id,
        jobApplicationId: event?.jobApplicationId,
        invitationid: invitation?.id,
        status: 'Accepted',
      });

      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t('pages.InterviewDashboard.interviewInvitationAccepted'),
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className={classes.Card}>
      <div className={classes.border} />
      <div
        className={classes.cardContent}
        onClick={() => onViewCandidate(event)}
      >
        <div className={classes.rowTop}>
          <div className={classes.avatar}>
            {event.userAvatar ? (
              <img
                src={getResizedImageUrl({
                  url: `avatar/${event.userAvatar}`,
                  width: 60,
                })}
                alt="Candidate"
              />
            ) : (
              <DefaultAvatar
                firstName={event?.userFirstName}
                lastName={event?.userLastName}
                width={60}
                height={60}
              />
            )}
          </div>
          <div className={classes.titleBlock}>
            <div className={classes.title}>
              {event?.userFirstName} {event?.userLastName}
            </div>
            <div className={classes.subtitle}>{event?.jobPost?.jobTitle}</div>
          </div>
          {adjustedStatus && (
            <div
              className={classNames(
                classes.badge,
                classes[adjustedStatus?.toLowerCase()]
              )}
            >
              {t(`common.externalInterviewStatuses.${adjustedStatus}`)}
            </div>
          )}
        </div>
        <div className={classes.rowBottom}>
          <div className={classes.meta}>
            <span className={classes.chip}>
              {isToday
                ? t('common.today')
                : moment(eventDate).format('DD MMM YYYY')}
            </span>
            {event?.time ? (
              <span className={classes.chip}>{event.time}</span>
            ) : null}
            {event?.event?.timezone ? (
              <span className={classes.chip}>{event?.event?.timezone}</span>
            ) : null}
          </div>
          <div className={classes.actions}>
            {adjustedStatus === 'New' || adjustedStatus === 'Rescheduled' ? (
              <AutoButton
                height={40}
                variant="black"
                onClick={(e) => {
                  e.stopPropagation();
                  declineInvite();
                }}
              >
                {t('pages.InterviewDashboard.declineCta')}
              </AutoButton>
            ) : null}
            {adjustedStatus === 'New' || adjustedStatus === 'Rescheduled' ? (
              <AutoButton
                height={40}
                onClick={(e) => {
                  e.stopPropagation();
                  acceptInvite();
                }}
              >
                {t('pages.InterviewDashboard.acceptCta')}
              </AutoButton>
            ) : null}
            {adjustedStatus === 'FeedbackDue' ? (
              <AutoButton
                height={40}
                onClick={(e) => {
                  e.stopPropagation();
                  showFeedbackModal({
                    candidate: event.candidate,
                    interview: event.event,
                    job: event.jobPost,
                    refetch,
                    isReviewing: false,
                    existingFeedback: null,
                  });
                }}
              >
                {t('pages.InterviewDashboard.feedbackCta')}
              </AutoButton>
            ) : null}

            {adjustedStatus === 'Accepted' ||
            adjustedStatus === 'Declined' ||
            adjustedStatus === 'Canceled' ||
            adjustedStatus === 'FeedbackSent' ||
            adjustedStatus === 'FeedbackFinal' ? (
              <AutoButton
                height={40}
                variant="blackWhite"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewCandidate(event);
                }}
              >
                {t('pages.InterviewDashboard.viewCta')}
              </AutoButton>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
